import { useState, useEffect, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { getStokGC, getStokGCOzet, getStokListesi, getStokGCYillar, getStokGCOzetStok } from "../services/api";

const AYLAR = [
  "", "Ocak", "Subat", "Mart", "Nisan", "Mayis", "Haziran",
  "Temmuz", "Agustos", "Eylul", "Ekim", "Kasim", "Aralik",
];

const fmt = (n) =>
  n == null
    ? "-"
    : Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " TL";

const fmtKisa = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return v.toFixed(0);
};

const TABS = [
  { id: "ozet", label: "Ozet", icon: "📊" },
  { id: "kayitlar", label: "Kayitlar", icon: "📋" },
  { id: "stok", label: "Stok Bazlı", icon: "📦" },
];

const card = { background: "var(--bg-card-alt)", border: "1px solid var(--accent-bg)", borderRadius: 10, padding: 20 };
const th = { padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "var(--text-muted)", borderBottom: "1px solid var(--border-hairline)", textTransform: "uppercase", letterSpacing: ".05em" };
const td = { padding: "11px 14px", fontSize: 13, color: "var(--text-primary)", borderBottom: "1px solid var(--border-hairline)" };
const inp = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "8px 11px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" };
const btn = (bg, color) => ({ background: bg, color, border: "none", borderRadius: 7, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" });
const chip = (active) => ({
  padding: "6px 13px", borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: "pointer",
  border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
  background: active ? "var(--accent-bg)" : "var(--bg-card)",
  color: active ? "var(--accent-text)" : "var(--text-secondary)",
});

export default function StokGelirGider() {
  const today = new Date();

  const [tab, setTab] = useState("ozet");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [secYil, setSecYil] = useState(today.getFullYear());
  const [secAy, setSecAy] = useState(0); // 0 = Tüm yıl
  const [tur, setTur] = useState(""); // "", "GELIR", "GIDER"

  const [ozet, setOzet] = useState([]);
  const [kayitlar, setKayitlar] = useState([]);

  // ── Stok Bazlı sekmesi ──
  const [stokSearch, setStokSearch] = useState("");
  const [stokDropdownOpen, setStokDropdownOpen] = useState(false);
  const [stoklar, setStoklar] = useState([]);
  const [loadingStoklar, setLoadingStoklar] = useState(false);
  const [selectedStok, setSelectedStok] = useState(null);

  const [yilSecenekleriStok, setYilSecenekleriStok] = useState([]);
  const [tumYillar, setTumYillar] = useState(true);
  const [secilenYillar, setSecilenYillar] = useState([]);
  const [tumAylar, setTumAylar] = useState(true);
  const [secilenAylar, setSecilenAylar] = useState([]);

  const [stokOzet, setStokOzet] = useState([]);
  const [stokLoading, setStokLoading] = useState(false);
  const [grafikTuru, setGrafikTuru] = useState("tumu"); // "tumu" | "gelir" | "gider"
  const [karsilastir, setKarsilastir] = useState(false);
  const [oncekiYilOzet, setOncekiYilOzet] = useState([]);

  const karsilastirmaMumkun = !tumYillar && secilenYillar.length === 1;
  const karsilastirmaAktif = karsilastir && karsilastirmaMumkun;

  useEffect(() => {
    // Ozet (KPI + grafik) her zaman yil/ay filtresine gore yuklenir
    loadOzet();
    if (tab === "kayitlar") loadKayitlar();
  }, [tab, secYil, secAy, tur]);

  function flash(text, type = "error") {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  }

  async function loadOzet() {
    setLoading(true);
    try {
      const params = { yil: secYil };
      if (secAy) params.ay = secAy;
      const res = await getStokGCOzet(params);
      setOzet(res.data.rows || []);
    } catch {
      flash("Ozet yuklenemedi");
    } finally {
      setLoading(false);
    }
  }

  async function loadKayitlar() {
    setLoading(true);
    try {
      const params = { yil: secYil };
      if (secAy) params.ay = secAy;
      if (tur) params.tur = tur;
      const res = await getStokGC(params);
      setKayitlar(res.data.rows || []);
    } catch {
      flash("Kayitlar yuklenemedi");
    } finally {
      setLoading(false);
    }
  }

  // ── Stok Bazlı sekmesi ──
  useEffect(() => {
    getStokGCYillar()
      .then((r) => setYilSecenekleriStok((r.data || []).slice().sort((a, b) => b - a)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!stokDropdownOpen) return;
    setLoadingStoklar(true);
    const t = setTimeout(() => {
      getStokListesi(stokSearch)
        .then((r) => setStoklar(r.data || []))
        .catch(() => setStoklar([]))
        .finally(() => setLoadingStoklar(false));
    }, 200);
    return () => clearTimeout(t);
  }, [stokSearch, stokDropdownOpen]);

  useEffect(() => {
    if (tab !== "stok") return;
    setStokLoading(true);
    getStokGCOzetStok(selectedStok?.STOK_KODU, tumYillar ? [] : secilenYillar, tumAylar ? [] : secilenAylar)
      .then((r) => setStokOzet(r.data.rows || []))
      .catch(() => flash("Stok özeti yüklenemedi"))
      .finally(() => setStokLoading(false));
  }, [tab, selectedStok, tumYillar, secilenYillar, tumAylar, secilenAylar]);

  useEffect(() => {
    if (tab !== "stok" || !karsilastirmaAktif) {
      setOncekiYilOzet([]);
      return;
    }
    const oncekiYil = secilenYillar[0] - 1;
    getStokGCOzetStok(selectedStok?.STOK_KODU, [oncekiYil], tumAylar ? [] : secilenAylar)
      .then((r) => setOncekiYilOzet(r.data.rows || []))
      .catch(() => {});
  }, [tab, karsilastirmaAktif, selectedStok, secilenYillar, tumAylar, secilenAylar]);

  function toggleYil(y) {
    setSecilenYillar((prev) => (prev.includes(y) ? prev.filter((x) => x !== y) : [...prev, y]));
  }
  function toggleAy(a) {
    setSecilenAylar((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  }

  const stokToplamGelir = stokOzet.reduce((s, r) => s + (r.TOPLAM_GELIR || 0), 0);
  const stokToplamGider = stokOzet.reduce((s, r) => s + (r.TOPLAM_GIDER || 0), 0);
  const stokNet = stokToplamGelir - stokToplamGider;

  const buYil = karsilastirmaAktif ? secilenYillar[0] : null;
  const gecenYil = karsilastirmaAktif ? secilenYillar[0] - 1 : null;
  const buYilGelirKey = buYil != null ? `${buYil} Gelir` : "Gelir";
  const buYilGiderKey = buYil != null ? `${buYil} Gider` : "Gider";
  const gecenYilGelirKey = gecenYil != null ? `${gecenYil} Gelir` : "Gelir (Geçen Yıl)";
  const gecenYilGiderKey = gecenYil != null ? `${gecenYil} Gider` : "Gider (Geçen Yıl)";

  const stokGrafikData = useMemo(() => {
    if (karsilastirmaAktif) {
      const ayMap = {};
      stokOzet.forEach((r) => {
        if (r.YIL !== buYil) return;
        ayMap[r.AY] = ayMap[r.AY] || {};
        ayMap[r.AY].buYilGelir = r.TOPLAM_GELIR || 0;
        ayMap[r.AY].buYilGider = r.TOPLAM_GIDER || 0;
      });
      oncekiYilOzet.forEach((r) => {
        if (r.YIL !== gecenYil) return;
        ayMap[r.AY] = ayMap[r.AY] || {};
        ayMap[r.AY].gecenYilGelir = r.TOPLAM_GELIR || 0;
        ayMap[r.AY].gecenYilGider = r.TOPLAM_GIDER || 0;
      });
      return Object.keys(ayMap)
        .map(Number)
        .sort((a, b) => a - b)
        .map((ay) => ({
          donem: AYLAR[ay] || ay,
          [buYilGelirKey]: Number((ayMap[ay].buYilGelir || 0).toFixed(2)),
          [buYilGiderKey]: Number((ayMap[ay].buYilGider || 0).toFixed(2)),
          [gecenYilGelirKey]: Number((ayMap[ay].gecenYilGelir || 0).toFixed(2)),
          [gecenYilGiderKey]: Number((ayMap[ay].gecenYilGider || 0).toFixed(2)),
        }));
    }
    return [...stokOzet]
      .sort((a, b) => (a.YIL - b.YIL) || (a.AY - b.AY))
      .map((r) => ({
        donem: `${AYLAR[r.AY] || r.AY} ${r.YIL}`,
        Gelir: Number((r.TOPLAM_GELIR || 0).toFixed(2)),
        Gider: Number((r.TOPLAM_GIDER || 0).toFixed(2)),
        Net: Number((r.NET || 0).toFixed(2)),
      }));
  }, [stokOzet, oncekiYilOzet, karsilastirmaAktif, buYil, gecenYil, buYilGelirKey, buYilGiderKey, gecenYilGelirKey, gecenYilGiderKey]);

  // Özet KPI'lar
  const toplamGelir = ozet.reduce((s, r) => s + (r.TOPLAM_GELIR || 0), 0);
  const toplamGider = ozet.reduce((s, r) => s + (r.TOPLAM_GIDER || 0), 0);
  const net = toplamGelir - toplamGider;

  // Grafik verisi — aya göre artan sırada
  const grafikData = [...ozet]
    .sort((a, b) => (a.YIL - b.YIL) || (a.AY - b.AY))
    .map((r) => ({
      donem: `${AYLAR[r.AY] || r.AY} ${r.YIL}`,
      Gelir: Number((r.TOPLAM_GELIR || 0).toFixed(2)),
      Gider: Number((r.TOPLAM_GIDER || 0).toFixed(2)),
      Net: Number((r.NET || 0).toFixed(2)),
    }));

  const yilSecenekleri = [today.getFullYear() - 2, today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {msg && (
        <div style={{
          padding: "11px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: msg.type === "error" ? "#3f1515" : "#0f3d20",
          border: `1px solid ${msg.type === "error" ? "#dc2626" : "#16a34a"}`,
          color: msg.type === "error" ? "#fca5a5" : "#86efac",
        }}>{msg.text}</div>
      )}

      {/* Sekmeler */}
      <div style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--border-hairline)" }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "8px 16px", fontSize: 14, fontWeight: tab === t.id ? 600 : 400,
            color: tab === t.id ? "var(--accent-text)" : "var(--text-muted)",
            borderBottom: tab === t.id ? "2px solid var(--accent)" : "2px solid transparent",
            marginBottom: -1,
          }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Filtreler */}
      {tab !== "stok" && (
        <div style={{ ...card, display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 5, fontWeight: 600 }}>YIL</div>
            <select style={{ ...inp, width: 100 }} value={secYil} onChange={(e) => setSecYil(+e.target.value)}>
              {yilSecenekleri.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 5, fontWeight: 600 }}>AY</div>
            <select style={{ ...inp, width: 130 }} value={secAy} onChange={(e) => setSecAy(+e.target.value)}>
              <option value={0}>Tum Yil</option>
              {AYLAR.slice(1).map((a, i) => <option key={i + 1} value={i + 1}>{a}</option>)}
            </select>
          </div>
          {tab === "kayitlar" && (
            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 5, fontWeight: 600 }}>TUR</div>
              <select style={{ ...inp, width: 130 }} value={tur} onChange={(e) => setTur(e.target.value)}>
                <option value="">Tumu</option>
                <option value="GELIR">Gelir</option>
                <option value="GIDER">Gider</option>
              </select>
            </div>
          )}
          <button onClick={() => { loadOzet(); if (tab === "kayitlar") loadKayitlar(); }} style={btn("var(--accent-bg)", "var(--accent-text)")}>
            Yenile
          </button>
        </div>
      )}

      {/* KPI kartları */}
      {tab !== "stok" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
          {[
            { label: "Toplam Gelir", value: fmt(toplamGelir), color: "#34d399", border: "#0d3b2e" },
            { label: "Toplam Gider", value: fmt(toplamGider), color: "#fb923c", border: "#431407" },
            { label: "Net", value: fmt(net), color: net >= 0 ? "#60a5fa" : "#fca5a5", border: "var(--accent-bg)" },
          ].map((k) => (
            <div key={k.label} style={{ ...card, padding: 16, borderColor: k.border }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: k.color, marginTop: 6 }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── ÖZET SEKMESİ ── */}
      {tab === "ozet" && (
        <>
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--accent-text)", marginBottom: 16 }}>
              Aylik Gelir / Gider
            </div>
            {loading ? (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>Yukleniyor...</div>
            ) : grafikData.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>Kayit bulunamadi.</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={grafikData} margin={{ top: 0, right: 20, bottom: 40, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="donem" tick={{ fill: "var(--text-secondary)", fontSize: 12 }} angle={-30} textAnchor="end" />
                  <YAxis tick={{ fill: "var(--text-secondary)", fontSize: 12 }} tickFormatter={fmtKisa} />
                  <Tooltip
                    contentStyle={{ background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 8 }}
                    labelStyle={{ color: "var(--text-primary)" }}
                    formatter={(v) => fmt(v)}
                  />
                  <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 13 }} />
                  <Bar dataKey="Gelir" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Gider" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Net" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg-sidebar)" }}>
                  {["Donem", "Toplam Gelir", "Toplam Gider", "Net"].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 32 }}>Yukleniyor...</td></tr>}
                {!loading && ozet.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 32 }}>Kayit bulunamadi.</td></tr>}
                {[...ozet].sort((a, b) => (b.YIL - a.YIL) || (b.AY - a.AY)).map((r, i) => (
                  <tr key={i}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-card-alt)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={{ ...td, fontWeight: 600 }}>{AYLAR[r.AY] || r.AY} {r.YIL}</td>
                    <td style={{ ...td, textAlign: "right", color: "#34d399", fontWeight: 600 }}>{fmt(r.TOPLAM_GELIR)}</td>
                    <td style={{ ...td, textAlign: "right", color: "#fb923c", fontWeight: 600 }}>{fmt(r.TOPLAM_GIDER)}</td>
                    <td style={{ ...td, textAlign: "right", color: (r.NET || 0) >= 0 ? "#60a5fa" : "#fca5a5", fontWeight: 600 }}>{fmt(r.NET)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── KAYITLAR SEKMESİ ── */}
      {tab === "kayitlar" && (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border-hairline)", fontSize: 12, color: "var(--text-muted)" }}>
            {loading ? "Yukleniyor..." : `${kayitlar.length} kayit listeleniyor`}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "var(--bg-sidebar)" }}>
                  {["Tur", "Donem", "Stok Kodu", "Stok Adi", "Cari", "Fis No", "Miktar", "Tutar"].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 32 }}>Yukleniyor...</td></tr>}
                {!loading && kayitlar.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 32 }}>Kayit bulunamadi.</td></tr>}
                {kayitlar.map((r, i) => (
                  <tr key={i}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-card-alt)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={td}>
                      <span style={{
                        padding: "3px 9px", borderRadius: 99, fontSize: 11, fontWeight: 600,
                        background: r.TUR === "GELIR" ? "#0d3b2e" : "#431407",
                        color: r.TUR === "GELIR" ? "#34d399" : "#fb923c",
                      }}>{r.TUR}</span>
                    </td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{AYLAR[r.AY] || r.AY} {r.YIL}</td>
                    <td style={{ ...td, color: "#60a5fa", fontWeight: 600 }}>{r.STOK_KODU}</td>
                    <td style={td}>{r.STOK_ADI}</td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{r.CARI_ISIM || r.CARI_KOD || "-"}</td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{r.FISNO || "-"}</td>
                    <td style={{ ...td, textAlign: "right" }}>{r.MIKTAR == null ? "-" : Number(r.MIKTAR).toLocaleString("tr-TR")}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 600, color: r.TUR === "GELIR" ? "#34d399" : "#fb923c" }}>{fmt(r.TUTAR)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── STOK BAZLI SEKMESİ ── */}
      {tab === "stok" && (
        <>
          <div style={{ ...card, display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Stok seçici */}
            <div style={{ position: "relative", maxWidth: 420 }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 5, fontWeight: 600 }}>STOK</div>
              <input
                value={stokDropdownOpen ? stokSearch : (selectedStok ? selectedStok.STOK_ADI : "")}
                onChange={(e) => setStokSearch(e.target.value)}
                onFocus={() => { setStokDropdownOpen(true); setStokSearch(""); }}
                onBlur={() => setTimeout(() => setStokDropdownOpen(false), 150)}
                placeholder="Stok ara veya seç..."
                style={{ ...inp, width: "100%" }}
              />
              {stokDropdownOpen && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
                  background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8,
                  maxHeight: 300, overflowY: "auto", zIndex: 30, boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
                }}>
                  {loadingStoklar && <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-faint)" }}>Yükleniyor...</div>}
                  {!loadingStoklar && stoklar.length === 0 && <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-faint)" }}>Stok bulunamadı</div>}
                  {stoklar.map((s) => (
                    <button
                      key={s.STOK_KODU}
                      onClick={() => { setSelectedStok(s); setStokDropdownOpen(false); setStokSearch(""); }}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                        width: "100%", textAlign: "left", padding: "9px 12px", border: "none",
                        borderBottom: "1px solid var(--border-hairline)",
                        background: selectedStok?.STOK_KODU === s.STOK_KODU ? "var(--accent-bg)" : "transparent",
                        color: selectedStok?.STOK_KODU === s.STOK_KODU ? "var(--accent-text)" : "var(--text-secondary)",
                        cursor: "pointer", fontSize: 13,
                      }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.STOK_ADI}</span>
                      <span style={{ fontSize: 11, color: "var(--text-faint)", flexShrink: 0 }}>{s.STOK_KODU}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Yıl seçici */}
            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, fontWeight: 600 }}>YIL</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button style={chip(tumYillar)} onClick={() => setTumYillar(true)}>Tüm Yıllar</button>
                {yilSecenekleriStok.map((y) => (
                  <button key={y} style={chip(!tumYillar && secilenYillar.includes(y))} onClick={() => { setTumYillar(false); toggleYil(y); }}>
                    {y}
                  </button>
                ))}
              </div>
            </div>

            {/* Ay seçici */}
            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, fontWeight: 600 }}>AY</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button style={chip(tumAylar)} onClick={() => setTumAylar(true)}>Tüm Aylar</button>
                {AYLAR.slice(1).map((a, i) => (
                  <button
                    key={i + 1}
                    style={chip(!tumAylar && secilenAylar.includes(i + 1))}
                    onClick={() => { setTumAylar(false); toggleAy(i + 1); }}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                {[
                  { label: "Toplam Gelir", value: fmt(stokToplamGelir), color: "#34d399", border: "#0d3b2e" },
                  { label: "Toplam Gider", value: fmt(stokToplamGider), color: "#fb923c", border: "#431407" },
                  { label: "Net", value: fmt(stokNet), color: stokNet >= 0 ? "#60a5fa" : "#fca5a5", border: "var(--accent-bg)" },
                ].map((k) => (
                  <div key={k.label} style={{ ...card, padding: 16, borderColor: k.border }}>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>{k.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: k.color, marginTop: 6 }}>{k.value}</div>
                  </div>
                ))}
              </div>

              <div style={card}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--accent-text)" }}>
                      {selectedStok ? selectedStok.STOK_ADI : "📦 Tüm Stoklar"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{selectedStok ? `${selectedStok.STOK_KODU} · ` : ""}Aylık Gelir / Gider</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button style={chip(grafikTuru === "tumu")} onClick={() => setGrafikTuru("tumu")}>Tümü</button>
                    <button style={chip(grafikTuru === "gelir")} onClick={() => setGrafikTuru("gelir")}>Sadece Gelir</button>
                    <button style={chip(grafikTuru === "gider")} onClick={() => setGrafikTuru("gider")}>Sadece Gider</button>
                    <button
                      style={{ ...chip(karsilastirmaAktif), opacity: karsilastirmaMumkun ? 1 : 0.4, cursor: karsilastirmaMumkun ? "pointer" : "not-allowed" }}
                      disabled={!karsilastirmaMumkun}
                      title={karsilastirmaMumkun ? "" : "Karşılaştırma için 'Tüm Yıllar' kapatıp tek bir yıl seçin"}
                      onClick={() => setKarsilastir((k) => !k)}
                    >
                      🔁 Geçen Yılla Karşılaştır
                    </button>
                  </div>
                </div>
                {stokLoading ? (
                  <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>Yükleniyor...</div>
                ) : stokGrafikData.length === 0 ? (
                  <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>Kayıt bulunamadı.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={stokGrafikData} margin={{ top: 16, right: 20, bottom: 40, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="donem" tick={{ fill: "var(--text-secondary)", fontSize: 12 }} angle={-30} textAnchor="end" />
                      <YAxis tick={{ fill: "var(--text-secondary)", fontSize: 12 }} tickFormatter={fmtKisa} />
                      <Tooltip
                        contentStyle={{ background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 8 }}
                        labelStyle={{ color: "var(--text-primary)" }}
                        formatter={(v) => fmt(v)}
                      />
                      <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 13 }} />
                      {karsilastirmaAktif ? (
                        <>
                          {grafikTuru !== "gider" && <Bar dataKey={buYilGelirKey} fill="#22c55e" radius={[4, 4, 0, 0]} />}
                          {grafikTuru !== "gider" && <Bar dataKey={gecenYilGelirKey} fill="#86efac" radius={[4, 4, 0, 0]} />}
                          {grafikTuru !== "gelir" && <Bar dataKey={buYilGiderKey} fill="#f59e0b" radius={[4, 4, 0, 0]} />}
                          {grafikTuru !== "gelir" && <Bar dataKey={gecenYilGiderKey} fill="#fde68a" radius={[4, 4, 0, 0]} />}
                        </>
                      ) : (
                        <>
                          {grafikTuru !== "gider" && <Bar dataKey="Gelir" fill="#22c55e" radius={[4, 4, 0, 0]} />}
                          {grafikTuru !== "gelir" && <Bar dataKey="Gider" fill="#f59e0b" radius={[4, 4, 0, 0]} />}
                          {grafikTuru === "tumu" && <Bar dataKey="Net" fill="var(--accent)" radius={[4, 4, 0, 0]} />}
                        </>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "var(--bg-sidebar)" }}>
                      {["Donem", "Toplam Gelir", "Toplam Gider", "Net"].map((h) => <th key={h} style={th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {stokLoading && <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 32 }}>Yükleniyor...</td></tr>}
                    {!stokLoading && stokOzet.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 32 }}>Kayıt bulunamadı.</td></tr>}
                    {[...stokOzet].sort((a, b) => (b.YIL - a.YIL) || (b.AY - a.AY)).map((r, i) => (
                      <tr key={i}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-card-alt)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                        <td style={{ ...td, fontWeight: 600 }}>{AYLAR[r.AY] || r.AY} {r.YIL}</td>
                        <td style={{ ...td, textAlign: "right", color: "#34d399", fontWeight: 600 }}>{fmt(r.TOPLAM_GELIR)}</td>
                        <td style={{ ...td, textAlign: "right", color: "#fb923c", fontWeight: 600 }}>{fmt(r.TOPLAM_GIDER)}</td>
                        <td style={{ ...td, textAlign: "right", color: (r.NET || 0) >= 0 ? "#60a5fa" : "#fca5a5", fontWeight: 600 }}>{fmt(r.NET)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
        </>
      )}
    </div>
  );
}
